var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useState, useRef, useImperativeHandle, forwardRef, } from 'react';
import shortid from 'shortid';
import { Title, Message, MessageWrapper } from '../../input';
import DateRangeInput from './Input/DateRangeInput';
import Calendar from './Calendar';
import { Container, CalendarContainer } from './DatePicker';
import dayjs, { isValidDateStr } from './day';
import { getDefaultPlaceholders, getDefaultClearTooltip, getDefaultInputTooltip, getCalendarBottomPosition, } from '.';
const DateRangePicker = (_a, ref) => {
    var { lang = 'en', format = 'YYYY-MM-DD', fromRef = React.createRef(), toRef = React.createRef(), inputWidth = 8, status = 'valid', title, renderMessages = [], block = false, disabled, values, defaultValues, placeholders, onRangeChange, disabledDate, allowSame = true, calendarAlign, calendarPosition = 'BOTTOM', isRangeMode = true, tooltipText, isAlwaysOpen = false } = _a, restProps = __rest(_a, ["lang", "format", "fromRef", "toRef", "inputWidth", "status", "title", "renderMessages", "block", "disabled", "values", "defaultValues", "placeholders", "onRangeChange", "disabledDate", "allowSame", "calendarAlign", "calendarPosition", "isRangeMode", "tooltipText", "isAlwaysOpen"]);
    const containerRef = useRef(null);
    const titleRef = useRef(null);
    const [open, setOpen] = useState(false); // indicate that calendar is opened or not
    const [isFocusOnToInput, setIsFocusOnToInput] = useState(false);
    const [inputValueFrom, setInputValueFrom] = useState(defaultValues && defaultValues.length === 2
        ? dayjs(lang, defaultValues[0]).format(format)
        : '');
    const [inputValueTo, setInputValueTo] = useState(defaultValues && defaultValues.length === 2
        ? dayjs(lang, defaultValues[1]).format(format)
        : '');
    const [selectedFrom, setSelectedFrom] = useState(defaultValues && defaultValues.length === 2
        ? dayjs(lang, defaultValues[0])
        : undefined);
    const [selectedTo, setSelectedTo] = useState(defaultValues && defaultValues.length === 2
        ? dayjs(lang, defaultValues[1])
        : undefined);
    let dateFrom;
    let dateTo;
    if (values && values.length === 2) {
        dateFrom = values[0] ? dayjs(lang, values[0]) : undefined;
        dateTo = values[1] ? dayjs(lang, values[1]) : undefined;
    }
    else {
        dateFrom = selectedFrom;
        dateTo = selectedTo;
    }
    const handleDateChange = useCallback((newDate) => {
        if (!fromRef || !fromRef.current || !toRef || !toRef.current)
            return;
        let shouldOpen = false;
        let newRange;
        if (fromRef.current.matches(':focus')) {
            setSelectedFrom(newDate);
            setInputValueFrom(newDate.format(format));
            if (!dateTo) {
                toRef.current.focus();
                return;
            }
            if (newDate.isAfter(dayjs(lang, inputValueTo, format), 'date')) {
                // if start date is after end date means we need to reset to's Input for selecting next new end date
                setInputValueTo('');
                toRef.current.focus();
                setSelectedTo(undefined);
                newRange = [newDate.toDate(), undefined];
                shouldOpen = true;
            }
            else {
                newRange = [newDate.toDate(), dateTo.toDate()];
            }
        }
        else if (toRef.current.matches(':focus')) {
            setSelectedTo(newDate);
            setInputValueTo(newDate.format(format));
            if (!dateFrom) {
                fromRef.current.focus();
                return;
            }
            newRange = [dateFrom.toDate(), newDate.toDate()];
        }
        if (onRangeChange)
            onRangeChange({ range: newRange });
        setOpen(shouldOpen);
    }, [
        fromRef,
        toRef,
        format,
        dateFrom,
        dateTo,
        lang,
        inputValueTo,
        onRangeChange,
    ]);
    const handleInputChange = useCallback((e) => {
        if (!fromRef || !fromRef.current || !toRef || !toRef.current)
            return;
        if (fromRef.current === e.target) {
            setInputValueFrom(e.target.value);
        }
        else if (toRef.current === e.target) {
            setInputValueTo(e.target.value);
        }
        const newDate = dayjs(lang, e.target.value, format);
        const isValidDate = isValidDateStr(e.target.value, format) &&
            !(disabledDate && disabledDate(newDate.toDate()));
        if (!isValidDate)
            return;
        if (fromRef.current === e.target) {
            setSelectedFrom(newDate);
            if (onRangeChange && dateTo)
                onRangeChange({ range: [newDate.toDate(), dateTo.toDate()] });
        }
        else if (toRef.current === e.target) {
            setSelectedTo(newDate);
            if (onRangeChange && dateFrom)
                onRangeChange({ range: [dateFrom.toDate(), newDate.toDate()] });
        }
    }, [
        dateFrom,
        dateTo,
        disabledDate,
        format,
        fromRef,
        lang,
        onRangeChange,
        toRef,
    ]);
    const handleClearBtnClick = useCallback(() => {
        setSelectedFrom(undefined);
        setSelectedTo(undefined);
        setInputValueFrom('');
        setInputValueTo('');
        setOpen(true);
        if (fromRef && fromRef.current)
            fromRef.current.focus();
        if (onRangeChange)
            onRangeChange({ range: undefined });
    }, [fromRef, onRangeChange]);
    const clearDateRange = () => {
        setSelectedFrom(undefined);
        setSelectedTo(undefined);
        setInputValueFrom('');
        setInputValueTo('');
        setOpen(false);
    };
    useImperativeHandle(ref, () => ({ clearDateRange }));
    const handleInputClick = useCallback(() => setOpen(true), []);
    useEffect(() => {
        if (!fromRef || !fromRef.current || !toRef || !toRef.current)
            return () => { };
        const fromInput = fromRef.current;
        const toInput = toRef.current;
        const handleOutsideClick = (e) => {
            // update new valid date when input blur (clicking outside)
            if (e.type === 'mousedown' &&
                e.target &&
                (!containerRef.current ||
                    containerRef.current.contains(e.target) ||
                    !e.target.isConnected)) {
                return;
            }
            setOpen(false); // close calendar view
            const from = dayjs(lang, inputValueFrom, format);
            const to = dayjs(lang, inputValueTo, format);
            // check range is a invalid format or disabled date
            const isValidRange = isValidDateStr(inputValueFrom, format) &&
                isValidDateStr(inputValueTo, format) &&
                (from.isBefore(to, 'date') ||
                    (allowSame && from.isSameOrBefore(to, 'date'))) &&
                !(disabledDate &&
                    (disabledDate(from.toDate()) || disabledDate(to.toDate())));
            if (!isValidRange) {
                // drop invalid range
                if (values && onRangeChange)
                    onRangeChange({ range: undefined });
                setIsFocusOnToInput(false);
                setSelectedFrom(undefined);
                setSelectedTo(undefined);
                setInputValueFrom('');
                setInputValueTo('');
            }
        };
        const focusToNext = (e) => {
            const keyCode = e.keyCode || e.which;
            if (keyCode === 13)
                toInput.focus();
        };
        const goBlur = (e) => {
            const keyCode = e.keyCode || e.which;
            if ((e.type === 'keydown' && keyCode === 9) ||
                (e.type === 'keyup' && keyCode === 13)) {
                // tab or enter
                toInput.blur();
                handleOutsideClick(e);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        fromInput.addEventListener('keyup', focusToNext);
        toInput.addEventListener('keydown', goBlur);
        toInput.addEventListener('keyup', goBlur);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            fromInput.removeEventListener('keyup', focusToNext);
            toInput.removeEventListener('keydown', goBlur);
            toInput.removeEventListener('keyup', goBlur);
        };
    }, [
        allowSame,
        disabledDate,
        format,
        fromRef,
        inputValueFrom,
        inputValueTo,
        lang,
        onRangeChange,
        toRef,
        values,
    ]);
    useEffect(() => {
        // focus to another input won't get latest disabledDate
        // so we update a state to trigger Calendar component re-render
        if (!toRef || !toRef.current)
            return () => { };
        const toElement = toRef.current;
        const handleFocus = () => setIsFocusOnToInput(true);
        const handleBlur = () => setIsFocusOnToInput(false);
        toElement.addEventListener('focus', handleFocus);
        toElement.addEventListener('blur', handleBlur);
        return () => {
            toElement.removeEventListener('focus', handleFocus);
            toElement.removeEventListener('blur', handleBlur);
        };
    }, [toRef]);
    useEffect(() => {
        let newDateFrom;
        let newDateTo;
        if (values && values.length === 2) {
            newDateFrom = values[0] ? dayjs(lang, values[0]).format(format) : '';
            newDateTo = values[1] ? dayjs(lang, values[1]).format(format) : '';
        }
        else {
            newDateFrom = inputValueFrom;
            newDateTo = inputValueTo;
        }
        setInputValueFrom(newDateFrom);
        setInputValueTo(newDateTo);
    }, [lang, format, values]); // eslint-disable-line react-hooks/exhaustive-deps
    return (React.createElement(Container, { ref: containerRef, block: block },
        title && (React.createElement(Title, { ref: titleRef, onClick: handleClearBtnClick }, title)),
        React.createElement(DateRangeInput, Object.assign({ lang: lang, fromRef: fromRef, toRef: toRef, selectedFrom: dateFrom, selectedTo: dateTo, valueFrom: inputValueFrom, valueTo: inputValueTo, width: inputWidth, block: block, status: status, disabled: disabled, placeholders: placeholders || getDefaultPlaceholders(lang), onChange: handleInputChange, onClick: handleInputClick, onClearBtnClick: handleClearBtnClick, tooltipDirection: calendarPosition === 'TOP' ? 'BOTTOM_CENTER' : 'TOP_CENTER', tooltipText: tooltipText ||
                getDefaultInputTooltip(lang).replace('{{format}}', format), clearBtnTooltipText: getDefaultClearTooltip(lang) }, restProps)),
        React.createElement(MessageWrapper, null, renderMessages.map(renderMessage => (React.createElement(Message, { status: status, withBullet: renderMessages.length > 1, key: shortid.generate() }, renderMessage())))),
        React.createElement(CalendarContainer, { top: title ? 4.25 : 2.75, bottom: getCalendarBottomPosition({
                containerRef,
                titleRef,
            }), open: !disabled && (open || isAlwaysOpen), onMouseDown: e => e.preventDefault(), calendarAlign: calendarAlign, calendarPosition: calendarPosition },
            React.createElement(Calendar, { isRangeMode: isRangeMode, lang: lang, open: !disabled && (open || isAlwaysOpen), isFocusOnToInput: isFocusOnToInput, selectedFrom: dateFrom, selectedTo: dateTo, onChange: handleDateChange, disabledDate: current => (isFocusOnToInput &&
                    dateFrom &&
                    dayjs(lang, current).isBefore(dateFrom, 'date')) ||
                    !!(disabledDate && disabledDate(current)) }))));
};
export default forwardRef(DateRangePicker);
//# sourceMappingURL=DateRangePicker.js.map